import { getFormValid, uiActions } from '../slices'

import {
  isCarryingMember,
  translateNominal,
  getFloorTrussValue,
  isCalculationAction,
} from '../../common/fns'
import { numberFormatted } from '../../../../utils/fns'
import debounce from 'lodash/debounce'
import { HANGER_TYPE, HLC_FLUSH } from '../../common/constants'

const transformMemberDimension = (member, memberType) => {
  const { type, width, depth, isRough } = member

  switch (type) {
    case 'solidSawn':
    case 'ledger': {
      const nominalValue = translateNominal(width, depth, isRough)

      return { ...member, ...nominalValue }
    }
    case 'floorTruss': {
      if (isCarryingMember(memberType)) {
        const floorTrussValue = getFloorTrussValue(width)

        return { ...member, ...floorTrussValue }
      } else return { ...member }
    }
    case 'ssteel': {
      return { ...member, depth: 5.5 }
    }
    case 'nailer':
    case 'wallSFlush':
    case 'wallSGap':
    case 'wallDFlush':
    case 'wallDFlushStud':
    case 'wallDGap': {
      const swappedValue = {
        thickness: type.includes('wall') ? 1.5 : width,
        width: depth,
        depth: 5.5,
      }

      return { ...member, ...swappedValue }
    }
    default: {
      return { ...member }
    }
  }
}

const calculationGraphicData = ({ dispatch, getState }) => {
  const input = getState().calculation.input

  const member = {
    header: input.header,
    girder: input.girder,
    joist: input.joist,
    truss: input.truss,
    jack: input.jack,
    leftHip: input.leftHip,
    rightHip: input.rightHip,
  }

  const { configuration, hangerType } = input.jobSettings
  const connectionType = input.settings.connectionType
  const hangerOptions = input.hangerOptions

  const { header, girder, joist, truss, jack, leftHip, rightHip } = Object.keys(member).reduce(
    (acc, key) => {
      const {
        type,
        width,
        depth,
        ply,
        topChord,
        thickness: flangeThickness,
        kingWidth,
        slopeAngle,
        skewAngle,
      } = transformMemberDimension(member[key], key)

      const _hasFormatHeight = [
        'ledger',
        'nailer',
        'wallSGap',
        'wallSFlush',
        'wallDGap',
        'wallDFlush',
        'wallDFlushStud',
      ].includes(type)

      const nailerTotalThick = ply * (flangeThickness ?? 0.5)
      const carryingHeight = _hasFormatHeight ? nailerTotalThick + depth : depth

      if (isCarryingMember(key)) {
        acc[key] = {
          type,
          width,
          height: carryingHeight,
          ply,
          flangeThickness,
          slope: -hangerOptions.topFlangeSlopedDownAngle,
          topChord,
          verticalWidth: kingWidth ?? 0,
        }
      } else {
        const getCarriedData = {
          default: {
            type,
            width,
            height: depth,
            ply,
            flangeThickness,
          },
          jack: {
            type,
            width,
            heel: depth,
            slope: slopeAngle,
            skew: 0,
            ply,
          },
          leftHip: {
            type,
            width,
            heel: depth,
            slope: -slopeAngle,
            skew: skewAngle,
            ply,
          },
          rightHip: {
            type,
            width,
            heel: depth,
            slope: -slopeAngle,
            skew: skewAngle,
            ply,
          },
        }

        acc[key] = getCarriedData[key] ?? getCarriedData.default
      }

      return acc
    },
    {},
  )

  const hanger = {
    hangerType,
    open: hangerOptions.topFlangeOpenClosedAngle,
    offset: hangerOptions.topFlangeOffset,
    flush:
      hangerType === HANGER_TYPE.FACE_MOUNT || hangerType === HANGER_TYPE.CONCEALED
        ? HLC_FLUSH.CENTER
        : hangerOptions.hlcFlush,
    slope: -hangerOptions.slopeAngle,
    skew: -hangerOptions.skewAngle,
  }

  const memberData = {
    joist: {
      connectionType,
      carrying: header,
      carried: joist,
      hanger,
    },
    truss: {
      connectionType,
      carrying: girder,
      carried: truss,
      hanger,
    },
    multiTruss: {
      connectionType,
      carrying: girder,
      carried: {
        centerTruss: jack,
        leftTruss: leftHip,
        rightTruss: rightHip,
        type: configuration.includes('center') ? jack.type : leftHip.type,
        skew: configuration.includes('left') ? leftHip.skew : rightHip.skew,
        verticalWidth: 0,
        configuration,
      },
      hanger,
    },
  }

  const formattedData = numberFormatted(memberData)

  dispatch(
    uiActions.setGraphicsData({
      ...(formattedData[connectionType] ?? formattedData.joist),
    }),
  )
}

const debouncingCalculation = debounce(calculationGraphicData, 500)

const createGraphicData = (store) => (next) => (action) => {
  next(action)

  const isValid = getFormValid(store.getState())
  if (isCalculationAction(action.type) && isValid) {
    debouncingCalculation(store)
  }
}

export default createGraphicData
